/**
 * EditWorkspaceHomepage — Rocky Backend Management System › Edit Workspace
 * Homepage. Route: `/admin-rbms/edit-homepage`.
 *
 * Configures the user-facing Workspace homepage across three regions
 * (Part 1 Hero / Part 2 Hot Questions / Part 3 Hot Topics). Each region toggles
 * between a dashed-outline preview and a solid inline editor; Publish persists
 * the config (per agent) via homepageConfigClient. The display side
 * (WorkspaceHome) reads the same config and renders from it.
 *
 * Figma: Rocky-Assistant BMS — Edit Workspace Homepage (nodes 3470:17565 /
 * 16941 / 18544 / 18029).
 */
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import BreadcrumbGroup from '@amzn/awsui-components-react/polaris/breadcrumb-group';
import Button from '@amzn/awsui-components-react/polaris/button';
import Spinner from '@amzn/awsui-components-react/polaris/spinner';
import { useAuth } from '../../context/AuthContext';
import SafeTrans from '../../i18n/SafeTrans';
import { ADMIN_PAGE_LDAP_GROUP } from '../../config';
import { getAgentInfo, useAgent } from '../../context/AgentContext';
import type { HomepageConfig, Part1Config } from './types';
import { getConfig, saveConfig } from './homepageConfigClient';
import { homepageConfigAgent, resolveIconSrc } from '../../components/WorkspaceHome/homepageConfig';
import { GatewayApiError } from '../../client/GatewayClient';
import { Part1Editor, Part2Editor, Part3Editor } from './parts';
import './EditWorkspaceHomepage.css';

type PartKey = 'part1' | 'part2' | 'part3';

// Fallbacks so the editor always has something to edit when a part is absent
// (e.g. Gateway returned a default config with only Part 1 populated).
const EMPTY_PART1: Part1Config = { titleSegments: [], subheading: '' };

export default function EditWorkspaceHomepage() {
  const navigate = useNavigate();
  const { t } = useTranslation('settings');
  const { ldap_groups } = useAuth();

  // Edit the homepage of the agent the admin has selected (UserPopover "Switch
  // Agent"), resolved through the same map the business homepage reads with.
  // To edit another agent's homepage, switch agent first.
  const { currentAgent } = useAgent();
  const agent = homepageConfigAgent(currentAgent);

  const [config, setConfig] = useState<HomepageConfig | null>(null);
  const [editing, setEditing] = useState<PartKey | null>(null);

  useEffect(() => {
    let cancelled = false;
    setConfig(null);
    setEditing(null);
    getConfig(agent).then((c) => {
      if (!cancelled) setConfig(c);
    });
    return () => {
      cancelled = true;
    };
  }, [agent]);

  if (!ldap_groups.includes(ADMIN_PAGE_LDAP_GROUP)) {
    return (
      <div className="rbms-ewh-denied">
        <h2>{t('rbms.deniedTitle')}</h2>
        <p>
          <SafeTrans t={t} i18nKey="rbms.deniedBody" values={{ group: ADMIN_PAGE_LDAP_GROUP }} components={{ code: <code /> }} />
        </p>
      </div>
    );
  }

  const persist = async (next: HomepageConfig) => {
    try {
      const saved = await saveConfig(next);
      setConfig(saved);
      setEditing(null);
    } catch (e) {
      // 409 = someone else published a newer version; reload latest so the
      // editor rebases on it rather than clobbering their change. Guard the
      // reload too — if it also fails, still surface a message instead of
      // letting the rejection escape and leaving the editor inconsistent.
      if (e instanceof GatewayApiError && e.statusCode === 409) {
        try {
          const latest = await getConfig(agent);
          setConfig(latest);
          setEditing(null);
          window.alert(t('homepageEditor.conflict'));
        } catch {
          window.alert(t('homepageEditor.reloadFailed'));
        }
      } else {
        window.alert(t('homepageEditor.saveFailed'));
      }
    }
  };

  const Section = ({ part, title, children }: { part: PartKey; title: string; children: ReactNode }) => (
    <section className={`rbms-ewh-section${editing === part ? ' rbms-ewh-section--editing' : ''}`}>
      {/* While editing, the editor renders its own "Part N" + Add header row
          (Figma), so suppress the section's own head then. */}
      {editing !== part && (
        <div className="rbms-ewh-section__head">
          <span className="rbms-ewh-section__title">{title}</span>
          {/* Edit stays clickable on any part that isn't currently open, so you
              can switch which part you edit (matches Figma). */}
          <Button variant="inline-link" iconName="edit" onClick={() => setEditing(part)}>
            {t('homepageEditor.edit')}
          </Button>
        </div>
      )}
      {children}
    </section>
  );

  return (
    <div className="rbms-ewh-page">
      <div className="rbms-ewh-header">
        <BreadcrumbGroup
          items={[
            { text: t('access.breadcrumbHome'), href: '/' },
            { text: t('access.breadcrumbRbms'), href: '/admin-rbms' },
            { text: t('rbms.homepageTitle'), href: '/admin-rbms/edit-homepage' },
          ]}
          onFollow={(e) => {
            e.preventDefault();
            navigate(e.detail.href);
          }}
        />
      </div>

      <div className="rbms-ewh-body">
        <div className="rbms-ewh-agent">
          <SafeTrans
            t={t}
            i18nKey="homepageEditor.editingFor"
            values={{ agent: getAgentInfo(agent).label }}
            components={{ strong: <strong /> }}
          />
        </div>
        {!config ? (
          <div className="rbms-ewh-loading"><Spinner /> {t('homepageEditor.loading')}</div>
        ) : (
          <div className="rbms-ewh-sections">
            <Section part="part1" title={t('homepageEditor.part', { n: 1 })}>
              {editing === 'part1' ? (
                <Part1Editor
                  value={config.part1 ?? EMPTY_PART1}
                  onCancel={() => setEditing(null)}
                  onPublish={(v) => persist({ ...config, part1: v })}
                />
              ) : (
                <div className="rbms-ewh-preview rbms-ewh-hero">
                  <div className="rbms-ewh-hero__title">
                    {(config.part1?.titleSegments?.length
                      ? config.part1.titleSegments
                      : [{ text: config.part1?.title ?? '' }]
                    ).map((s, i) => (
                      <span key={i} style={{ color: s.color || undefined }}>
                        {s.text}
                      </span>
                    ))}
                  </div>
                  <div className="rbms-ewh-hero__sub">
                    {(config.part1?.subheadingSegments?.length
                      ? config.part1.subheadingSegments
                      : [{ text: config.part1?.subheading ?? '' }]
                    ).map((s, i) => (
                      <span key={i} style={{ color: s.color || undefined }}>
                        {s.text}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </Section>

            <Section part="part2" title={t('homepageEditor.part', { n: 2 })}>
              {editing === 'part2' ? (
                <Part2Editor
                  value={config.part2 ?? []}
                  onCancel={() => setEditing(null)}
                  onPublish={(v) => persist({ ...config, part2: v })}
                />
              ) : (
                <div className="rbms-ewh-preview rbms-ewh-chips">
                  {(config.part2 ?? []).map((q) => {
                    const tint = q.badges?.includes('NEW')
                      ? ' rbms-ewh-chip--new'
                      : q.badges?.includes('HOT')
                        ? ' rbms-ewh-chip--hot'
                        : '';
                    return (
                      <span key={q.id} className={`rbms-ewh-chip${tint}`}>
                        {q.badges?.includes('NEW') && <span className="rbms-ewh-chip-badge rbms-ewh-chip-badge--new">{t('homepageEditor.badgeNew')}</span>}
                        {q.badges?.includes('HOT') && <span className="rbms-ewh-chip-badge rbms-ewh-chip-badge--hot">{t('homepageEditor.badgeHot')}</span>}
                        {q.uiCopy}
                      </span>
                    );
                  })}
                </div>
              )}
            </Section>

            <Section part="part3" title={t('homepageEditor.part', { n: 3 })}>
              {editing === 'part3' ? (
                <Part3Editor
                  value={config.part3 ?? []}
                  agent={agent}
                  onCancel={() => setEditing(null)}
                  onPublish={(v) => persist({ ...config, part3: v })}
                />
              ) : (
                <div className="rbms-ewh-preview rbms-ewh-topics">
                  {(config.part3 ?? []).map((topic) => (
                    <div key={topic.id} className="rbms-ewh-topic">
                      <span className="rbms-ewh-topic__icon">
                        {topic.iconUrl ? <img src={resolveIconSrc(topic.iconUrl)} alt="" /> : null}
                      </span>
                      <div className="rbms-ewh-topic__title">
                        <span className="rbms-ewh-topic__headline">
                          {topic.badges?.includes('NEW') && <span className="rbms-ewh-badge rbms-ewh-badge--new">{t('homepageEditor.badgeNew')}</span>}
                          {topic.badges?.includes('HOT') && <span className="rbms-ewh-badge rbms-ewh-badge--hot">{t('homepageEditor.badgeHot')}</span>}
                          {topic.title}
                        </span>
                      </div>
                      {topic.description && <div className="rbms-ewh-topic__desc">{topic.description}</div>}
                      {topic.action === 'navigate' && topic.href && (
                        <div className="rbms-ewh-topic__desc">→ {topic.href}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Section>
          </div>
        )}
      </div>
    </div>
  );
}
